import { CommonMaterialUniforms } from './CommonMaterialUniforms.js';
import { MaterialUniformBuffer } from './MaterialUniformBuffer.js';
import { ObjectUniformBuffer, MAX_BATCH } from './ObjectUniformBuffer.js';
import { ModelUniformUpdater } from './ModelUniformUpdater.js';
import { OBJECT_STRIDE_32, OBJECT_STRIDE } from './ObjectUniforms.js';
import { GPUSceneRenderer } from '../../gpudrivenrender/GPUSceneRenderer.js';

/** @import { MultiDrawBatch } from '../../gpudrivenrender/MultiDrawBatch.js' */

// find modelId of a RenderBatch
const getModelId = (renderBatch) => {
  const fragList = renderBatch.frags;
  return fragList?.modelId ?? 0;
};

/**
 * Manages bindGroups and batched uniform buffers for each RenderBatch.
 *
 * There are 3 types of UniformBuffers:
 *
 *  1. ObjectUniformBuffer:    Uniforms that vary per object (e.g. model matrix a themingColor etc.)
 *  2. MaterialUniformBuffer:  Uniforms that vary per material (e.g. color, texture, etc.). Entries are referenced by object uniforms.
 *  3. CommonMaterialUniforms: Some values that are just set once for each scene.
 *
 * For 1., there are two variants:
 *
 *  - Pre-Batched: The uniforms for a whole RenderBatch are uploaded just once in advance and updated on changes.
 *                 We do this for all model geometry. A RenderBatch with pre-batched uniforms has a range
 *                 in the GPU-side uniform buffer, stored in ObjectUniformBuffer
 *                 (see ObjectUniformBufferRange type in ObjectUniformBuffer.js).
 *
 *  - Dynamic:     The uniforms are uploaded per frame. We have a small shared object uniform buffer, enough for MAX_BATCH objects.
 *                 This buffer is used when rendering anything but actual models, e.g. scene, sceneAfter, overlays, ...
 *                 If this buffer is used, uniforms need to be set for every rendered object, via the setOne... methods.
 *                 It's a shared buffer that is constantly rewritten.
 *                 There's a CPU-side buffer of the same size that is used to aggregate object data and push it into the GPU buffer.
 *
 *  A render pass needs to determine if it's currently rendering a model scene (i.e. RenderBatch) or one of the other scenes.
 *
 *  1) Rendering a non-model scene: (using dynamic uniforms)
 *      - The bind group needs to be obtained via getBindGroup() (optionally provide -1 as the only parameter)
 *      - Object and material uniforms need to be set via the setOne... methods for every object
 *      - writeToQueue needs to be called prior to submitting the render command buffer
 *      - Each render command buffer may only contain draw calls for up to MAX_BATCH objects
 *
 *  2) Rendering a model scene: (using pre-batched uniforms)
 *      - The bind group needs to be obtained via getBindGroup(renderBatch)
 *      - Renderbatches may be flagged as dirty, requiring batched uniform updates;
 *        updateBatch needs to be called in this case
 *      - Materials need to be set up (once) via initMaterialUpdateHook; object uniforms do not need to be set
 *      - A material's needsUpdate flag needs to be reset after the pipeline's draw function has been called
 *      - writeToQueue does not need to be called
 *      - Render command buffers may contain draw calls for an unlimited number of objects
 */
export class RenderBatchUniforms {

  /** @type GPUDevice */
  #device;

  // Manages buffers for all pre-uploaded uniform batches.
  /** @type {ObjectUniformBuffer} */
  #batchedUniforms;

  // A separate UniformStorage with a single fixed-size buffer. This is used for the cases where we have to
  // upload the object-uniforms per-frame. This is done whenever we don't have RenderBatches or THREE.Scenes
  // that don't have pre-uploaded uniform batches. (currently for overlays and 2D geometry).
  /** @type {ObjectUniformBuffer} */
  #dynamicUniforms;
  /** @type {GPUBindGroup} */
  #dynamicUniformsBindGroup;

  // Common uniforms that are shared across all objects of a scene
  /** @type {CommonMaterialUniforms} */
  #commonMaterialUniforms;

  // Per-material uniforms that can be shared across multiple objects and models.
  /** @type {MaterialUniformBuffer} */
  #materialUniforms;

  // Same for all bindGroups
  /** @type {GPUBindGroupLayout} */
  #bindGroupLayout;

  // Each ObjectUniformBuffer instance can manage one or more buffers.
  // For each of these buffers, we need a separate bindGroup.
  // #bindGroup[buffer.id] provides the bindGroup for the given buffer.id.
  /** @type {Map<number, GPUBindGroup>} */
  #bindGroups = new Map();

  // For updating individual fragId uniforms, we need to find where they are stored in the GPU-side UniformBuffers.
  // This is managed by the modelUniformUpdaters.
  #modelUniformUpdaters = new Map(); // model id -> uniform updater instance

  // Needed by MainPass to know how many shapes can be rendered in one-go without intermediate uniform-buffer uploads.
  // Only relevant for per-frame-uploaded uniforms (overlays, THREE.Scene, and 2D geometry).
  MAX_BATCH = MAX_BATCH;

  // When rendering postMaterial overlays, there is some code in MainPass that addresses a materialReference
  // using a float offset. (see setMaterialReference)
  // Note: This is just for legacy compatibility with old ObjectUniforms and should finally use an object index directly.
  OBJECT_STRIDE_32 = OBJECT_STRIDE_32;

  // Only needed by ModelUpdaters to invalidate RenderBundles.
  /** @type {Renderer} */
  #renderer;

  // TODO: The props below describe a "current state" that is silently set by "getBindGroup". They will probably go away
  //       if we remove the statefulness and make getRenderIndex() just take the RenderBatch directly.

  // Used during rendering a previously uploaded RenderBatch
  //  - Before starting the batch, it is set to the offset in the GPU-side uniform buffer where the batch uniforms start.
  //  - During batch rendering, it used to convert between "objectIndex" (= index within the batch) and renderIndex (= index in the GPU-side buffer where the uniform data is stored)
  #currentRangeStartGPU = 0;

  // During rendering a pre-uploaded batch, this index is used to convert between objectIndex and renderIndex.
  // objectIndex is the loopIndex within the batch, i.e., starting at batch.start.
  #currentBatchStartIndex = 0;

  /** @type {GPUBuffer} */
  #dummyInstanceIdMap;

  /** @type {import('../../ShaderExtension').ShaderExtension[]} */
  #shaderExtensions = [];

  #layoutVersion = 0;

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {

    // Note that the renderer is only needed to invalidate RenderBundles when a material changes. All other code here
    // actually just needs the WebGPU device.
    this.#renderer = renderer;
    this.#device = renderer.getDevice();

    this.#commonMaterialUniforms = new CommonMaterialUniforms(this.#device);
    this.#materialUniforms = new MaterialUniformBuffer(this.#device);

    this.#initBindGroupLayout();

    this.#dummyInstanceIdMap = this.#device.createBuffer({
      size: 4, // small non-zero size
      usage: GPUBufferUsage.STORAGE
    });

    // Initiale dynamic uniform storage and corresponding bind group
    this.#dynamicUniforms = new ObjectUniformBuffer(this.#device, this.#materialUniforms, this.MAX_BATCH * OBJECT_STRIDE);
    const gpuBuffer = this.#dynamicUniforms.getGPUBuffer();
    this.#dynamicUniformsBindGroup = this.createBindGroup(gpuBuffer, this.#dummyInstanceIdMap);

    this.#batchedUniforms = new ObjectUniformBuffer(this.#device, this.#materialUniforms);
  }

  /**
   * Upload uniform data for the given RenderBatch.
   * The allocated GPUBufferRange is attached to a range in an ObjectUniformBuffer.
   * For later uploads, the range is reused if possible.
   * Note:
   *    For some fragments, materials may be undefined. This may happen if the fragment is not fully loaded yet.
   *    This is not a problem, but these specific fragments will not be rendered until their material is set via setMaterial() later.
   * @param {RenderBatch} renderBatch
   */
  updateBatch(renderBatch) {

    // First, we need to make sure that a range of appropriate size is allocated in the GPU-side UniformBuffer.
    let range = this.#batchedUniforms.getRange(renderBatch);

    // If there is an allocated range, but it has wrong size, discard it first
    if (range && range.rangeLength !== renderBatch.count) {
      this.freeUniforms(renderBatch);
      range = null;
    }

    // Make sure a range of proper size is allocated
    if (!range) {
      // Allocate a new GPU range
      range = this.#batchedUniforms.allocUniformBufferRange(renderBatch);

      // Register the RenderBatch at the modelUniformUpdater, so that we can find the uploaded
      // uniforms for each fragment in this RenderBatch.
      this.#registerRenderBatch(renderBatch);
    }

    // Note:
    //  - In most cases, updateCount is identical with renderBatch.count. However, it may be smaller
    //    for incomplete RenderBatches during loading.
    //  - Despite of the name, lastItem itself is NOT part of the range, so the we don't have to add 1.
    const updateCount = renderBatch.lastItem - renderBatch.start;

    this.#batchedUniforms.updateRange(renderBatch, renderBatch.start, updateCount);
  }

  freeUniforms(renderBatch) {
    if (!this.#batchedUniforms.getRange(renderBatch)) {
      return;
    }

    this.#batchedUniforms.freeUniformBufferRange(renderBatch);
    // Tell modelUniformUpdater that we don't need any GPU-side updates anymore
    // if fragments in this batch change.
    this.#unregisterRenderBatch(renderBatch);
  }

  /**
   * Prepares rendering of the given batch. It is required/assumed that the RenderBatch was uploaded before
   * using uploadBatchUniforms().
   * @param {RenderBatch|undefined} [renderBatch] - If not provided, we return the bindGroup for dynamic uniforms.
   * @returns {GPUBindGroup} - The bindGroup that needs to be set for rendering this batch
   */
  getBindGroup(renderBatch) {
    if (renderBatch) {
      const mdBatch = GPUSceneRenderer.maybeGetMultiDrawBatch(renderBatch);
      if (mdBatch && mdBatch.renderBatchBg) {
        if (mdBatch._bgLayoutVersion === this.#layoutVersion) {
          return mdBatch.renderBatchBg;
        }
        // Layout changed (e.g. extension added/removed) — recreate bind group
        const range = this.#batchedUniforms.getRange(renderBatch);
        if (range) {
          mdBatch.renderBatchBg = this.createBindGroup(
            range.memoryBlock.buffer, mdBatch.visibleInstanceIdsBuffer);
          mdBatch._bgLayoutVersion = this.#layoutVersion;
          return mdBatch.renderBatchBg;
        }
      }
    }

    const range = this.#batchedUniforms.getRange(renderBatch);

    // If we don't have pre-batched uniform, use the bindGroup for dynamic uniforms.
    if (!range) {
      // If we call without a RenderBatch or the RenderBatch doesn't provide pre-batched
      // uniforms, we  use bindGroup for dynamic uniforms. The state variables
      // are not needed and are just reset in this case.
      this.#currentRangeStartGPU = 0;
      this.#currentBatchStartIndex = 0;
      return this.#dynamicUniformsBindGroup;
    }

    // The Uniforms for the renderBatch are stored in a range within the GPU-side uniform buffer
    // Set the current rangeStart to the start of that range.
    this.#currentRangeStartGPU = range.startIndex;

    // Store the range startIndex. This is the objectIndex of the first object in the batch.
    this.#currentBatchStartIndex = renderBatch.start | 0;

    // get bind group pointing to the bufffer that contains the uniforms for this batch
    const bindGroup = this.#initBindGroup(range);
    return bindGroup;
  }

  /**
   * Converts the objectIndex within a batch to the corresponding renderIndex in the GPU-side uniform buffer.
   * @param {number} objectIndex       - The loopIndex provided while rendering an object. While rendering a batch, the passed objectIndex values [batch.start, ..., batch.start.count]
   * @returns {number} The renderIndex - pointing to the corresponding entry in the GPU uniform buffer
   */
  getRenderIndex(objectIndex) {
    return objectIndex - this.#currentBatchStartIndex + this.#currentRangeStartGPU;
  }

  // @returns {GPUBindGroupLayout}
  getLayout() {
    return this.#bindGroupLayout;
  }

  getLayoutVersion() {
    return this.#layoutVersion;
  }

  // Adding a model before rendering ensures that a ModelUniformUpdater is created to make sure
  // that changes on fragments (e.g. material) trigger updates of the corresponding GPU-side uniforms.
  addModel(model) {

    // For 2D models, we currently upload uniforms per-frame. No need to create uniform storage.
    if (model.is2d()) {
      return;
    }

    // Create UniformUpdater to handle fragment changes
    this.#modelUniformUpdaters.set(model.id, new ModelUniformUpdater(model, this.#batchedUniforms, this.#renderer));
  }

  /**
   * Clean up object uniform data for a model.
   * @param {RenderModel} model The model to clean up resources for.
   */
  removeModel(model) {

    const modelKnown = this.#modelUniformUpdaters.has(model.id);
    if (!modelKnown) {
      // If we can't find any storage, this model was never
      // added and we can stop here.
      return;
    }

    // Disconnect any model-related callbacks
    const updater = this.#modelUniformUpdaters.get(model.id);
    this.destroyUpdater(updater);
    // Remove map entries for this model
    this.#modelUniformUpdaters.delete(model.id);
  }

  /**
   * @param {ModelUniformUpdater} updater
   */
  destroyUpdater(updater) {
    // Note: We don't free uniforms here, because...
    //  a) This is responsibility of OutOfCoreTileManager
    //  b) Freeing it directly here would actually bypass the memory tracking of OutOfCoreTileManager
    updater.dtor();
  }

  /**
   * @returns {CommonMaterialUniforms} - Access to uniforms that are shared across all objects of a scene.
   */
  getCommonMaterialUniforms() {
    return this.#commonMaterialUniforms;
  }

  /**
   * @returns {ObjectUniformBuffer} - Access to the storage used for uniforms that are uploaded per frame.
   */
  getDynamicUniforms() {
    return this.#dynamicUniforms;
  }

  //
  // Legacy wrapper for compatibility with old ObjectUniforms.
  // Most of them just forward to the underlying handlers, so we can get rid of them after removing the old ObjectUniforms.
  //

  // Todo: This is just a legacy wrapper for compabibility with the old ObjectUniforms
  //       Actually, main pass could use dynamicUniforms directly.
  setOneObjectData(mesh, objectIndex) {
    this.#dynamicUniforms.setOneObjectCPU(mesh, objectIndex);
  }

  // Update material for a single object (if changed). Only used for scenes that don't have pre-uploaded uniform batches.
  setOneMaterialData(material, materialTextureMask) {
    this.#materialUniforms.updateMaterial(material, materialTextureMask);
  }

  setOneMaterialData2D(material) {
    this.#materialUniforms.updateMaterial2D(material);
  }

  // Flush dynamic uniforms to GPU after setting items [0,...itemsAdded-1] via setOneObjectData.
  writeToQueue(itemsAdded) {
    this.#dynamicUniforms.writeToQueue(itemsAdded);
  }


  resetUpdateHeuristic(modelId) {
    this.#modelUniformUpdaters.get(modelId)?.resetUpdateHeuristic();
  }

  setEdgeColorInt(color) {
    this.#commonMaterialUniforms.setEdgeColorInt(color);
  }

  setDoNotCutOverride(value) {
    this.#commonMaterialUniforms.setDoNotCutOverride(value);
  }

  initMaterialUpdateHook(material, materialTextureMask) {
    this.#materialUniforms.initMaterialUpdateHook(material, materialTextureMask);
  }

  // Direct override of a materialReference for a single object.
  // Used when rendering overlays with override materials.
  // Note that this only sets the value CPU side but doesn't upload yet
  setMaterialReference(objectIndex, material) {
    const materialRef = this.#materialUniforms.acquireMaterial(material);
    const writer = this.#dynamicUniforms.getUniformBuilder();
    writer.setMaterialReference(objectIndex, materialRef);
  }

  /**
   * @returns {number} Total byteSize on GPU. Note that we track actually used memory, not including extra memory due to paging.
   */
  byteSize() {
    const batchUniformBytes = this.#batchedUniforms.byteSize();
    const dynamicUniformsBytes = this.#dynamicUniforms.byteSize();
    const materialUniformBytes = this.#materialUniforms.byteSize();
    const commonMaterialUniforms = this.#commonMaterialUniforms.byteSize();
    return batchUniformBytes + dynamicUniformsBytes + materialUniformBytes + commonMaterialUniforms;
  }

  /**
   * Set the object data for a range of objects by getting the data from an instanced mesh.
   *  @param {THREE.Mesh} instancedMesh
   *  @param {number}     itemOffset
   */
  setObjectDataFromInstanceBuffer(instancedMesh, itemOffset) {
    this.#dynamicUniforms.setObjectDataFromInstanceBuffer(instancedMesh, itemOffset);
  }

  /**
   * If the uniforms for the given renderBatch are available in a GPUBuffer, this function returns the
   * ObjectUniformBufferRange with information about where these uniforms are stored (a range within a GPUBuffer).
   * @param {RenderBatch} renderBatch
   * @returns {ObjectUniformBufferRange|undefined}
   */
  getGPUBufferRange(renderBatch) {
    return this.#batchedUniforms.getRange(renderBatch);
  }

  //
  // Private methods
  //

  /**
   * Called by the Renderer when shader extensions are added/removed.
   * Rebuilds bind group layout and invalidates all bind groups.
   * @param {import('../../ShaderExtension').ShaderExtension[]} extensions
   */
  setShaderExtensions(extensions) {
    // Remove callbacks from previous extensions
    for (const ext of this.#shaderExtensions) {
      ext.onBindGroupInvalidated = null;
    }

    this.#shaderExtensions = extensions;

    // Set up buffer-change callbacks on new extensions
    for (const ext of extensions) {
      ext.onBindGroupInvalidated = () => {
        this.#invalidateAllBindGroups();
      };
    }

    this.#initBindGroupLayout();
    this.#invalidateAllBindGroups();
  }

  /**
   * Calls uploadForBatch on all extensions that need per-batch upload.
   * @param {*} renderBatch
   */
  uploadExtensionsForBatch(renderBatch) {
    if (this.#shaderExtensions.length === 0) return;

    const range = this.#batchedUniforms.getRange(renderBatch);
    const instanceOffset = range ? range.startIndex : 0;
    for (const ext of this.#shaderExtensions) {
      if (ext.needsPerBatchUpload()) {
        ext.uploadForBatch(renderBatch, instanceOffset);
      }
    }
  }

  /**
   * @returns {boolean} True if any registered extension needs per-batch upload.
   */
  anyExtensionNeedsPerBatchUpload() {
    for (const ext of this.#shaderExtensions) {
      if (ext.needsPerBatchUpload()) {
        return true;
      }
    }
    return false;
  }

  #invalidateAllBindGroups() {
    if (this.#dynamicUniforms) {
      this.#dynamicUniformsBindGroup = this.createBindGroup(
        this.#dynamicUniforms.getGPUBuffer(), this.#dummyInstanceIdMap
      );
    }
    this.#bindGroups.clear();
  }

  #initBindGroupLayout() {
    const entries = [
    {
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: {
        type: "read-only-storage"
      }
    },
    {
      binding: 1,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: {
        type: "read-only-storage"
      }
    },
    {
      binding: 2,
      visibility: GPUShaderStage.FRAGMENT,
      buffer: {
        type: "uniform"
      }
    },
    {
      binding: 3, // instanceIdMap
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: {
        type: "read-only-storage"
      }
    }];


    for (const ext of this.#shaderExtensions) {
      entries.push(...ext.getBindGroupLayoutEntries());
    }

    this.#bindGroupLayout = this.#device.createBindGroupLayout({ entries });
    this.#layoutVersion++;
  }

  /**
   * @param {GPUBuffer} objectUniformsBuffer
   * @param {GPUBuffer} instanceIdMapBuffer
   * @returns {GPUBindGroup}
   */
  createBindGroup(
  objectUniformsBuffer,
  instanceIdMapBuffer)
  {
    const entries = [
    {
      binding: 0,
      resource: {
        buffer: objectUniformsBuffer
      }
    },
    {
      binding: 1,
      resource: {
        buffer: this.#materialUniforms.getGPUBuffer()
      }
    },
    {
      binding: 2,
      resource: {
        buffer: this.#commonMaterialUniforms.getGPUBuffer()
      }
    },
    {
      binding: 3,
      resource: {
        buffer: instanceIdMapBuffer
      }
    }];


    for (const ext of this.#shaderExtensions) {
      entries.push(...ext.getBindGroupEntries());
    }

    return this.#device.createBindGroup({
      layout: this.#bindGroupLayout,
      entries
    });
  }

  // After upload, a RenderBatch must be registered at the modelUniformUpdater. This is needed to
  // track for each fragment where to find its uniform in the UniformBuffer.
  #registerRenderBatch(renderBatch) {
    const modelId = getModelId(renderBatch);
    const updater = this.#modelUniformUpdaters.get(modelId);
    updater.registerRenderBatch(renderBatch);
  }

  /**
   * After a RenderBatch is no longer needed, it must be unregistered at the modelUniformUpdater.
   * @param {RenderBatch} renderBatch
   */
  #unregisterRenderBatch(renderBatch) {
    const modelId = getModelId(renderBatch);
    const updater = this.#modelUniformUpdaters.get(modelId);
    if (updater) {
      updater.unregisterRenderBatch(renderBatch);
    }
  }

  /**
   * Returns the bindGroup for the given RenderBatch range's buffer, creating
   * it on-demand if it doesn't exist.
   * @param {ObjectUniformBufferRange} range
   * @returns {GPUBindGroup|null} - The bind group providing using the specified ObjectUniform buffer.
   */
  #initBindGroup(range) {
    const buffer = range.memoryBlock.buffer;
    if (!buffer) {
      console.error("TODO: Specified UniformBuffer does not exist.");
      return null;
    }

    // Make sure that a bindGroup exist for this UniformBuffer
    let bindGroup = this.#bindGroups.get(buffer.id);
    if (!bindGroup) {
      bindGroup = this.createBindGroup(buffer, this.#dummyInstanceIdMap);
      this.#bindGroups.set(buffer.id, bindGroup);
    }
    return bindGroup;
  }

  getGPUBuffer(renderBatch) {
    const range = this.getGPUBufferRange(renderBatch);
    return range.memoryBlock.buffer;
  }

  dtor() {
    this.#dynamicUniforms.dtor();
    this.#dynamicUniforms = null;
    this.#batchedUniforms.dtor();
    this.#batchedUniforms = null;
    this.#bindGroups = null;
    this.#dummyInstanceIdMap.destroy();
    this.#dummyInstanceIdMap = null;
    this.#commonMaterialUniforms.dtor();
    this.#commonMaterialUniforms = null;
    this.#materialUniforms.dtor();
    this.#materialUniforms = null;

    for (const [modelId, updater] of this.#modelUniformUpdaters) {
      this.destroyUpdater(updater);
    }
    this.#modelUniformUpdaters.clear();
    this.#modelUniformUpdaters = null;
  }
}